// src/components/LegalPage.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { termsOfService, privacyPolicy } from './legal-docs';
import { GA, gaSoftCard, GaStyle } from './glonetTheme';

// Helper function to convert markdown to basic HTML
const convertMarkdownToHTML = (markdown) => {
  // Replace headers
  let html = markdown
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold my-4">$1</h1>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold my-3">$1</h2>')
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold my-2">$1</h3>')
    // Replace lists
    .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>');

  // Wrap each RUN of consecutive list items in one <ul>. The old rule matched
  // items two at a time, so in any list of three or more the odd ones fell
  // outside the <ul> and rendered with visibly different spacing.
  html = html.replace(
    /(?:<li class="ml-4">.*<\/li>\n?)+/g,
    (run) => `<ul class="list-disc ml-6 my-2">\n${run.trim()}\n</ul>`);

  html = html
    // Replace paragraphs
    .split('\n\n').join('</p><p class="my-2">')
    // Wrap in paragraph tags
    .replace(/<\/p><p/g, '</p>\n<p');

  return `<p class="my-2">${html}</p>`;
};

const LegalPage = ({ defaultTab = null }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || 'tos'); // 'tos' or 'privacy'
  const navigate = useNavigate();
  const params = useParams();
  // The signup form links here with target="_blank", so this usually opens in
  // a fresh tab where there is nothing to go back TO — navigate(-1) did
  // nothing and the button just looked broken. A tab opened with _blank starts
  // with a history length of 1; anything else (in-app navigation, or a normal
  // link followed in the same tab) has somewhere real to return to.
  const canGoBack = window.history.length > 1;

  // Set the correct tab based on URL params if provided
  useEffect(() => {
    if (params.tab === 'privacy') {
      setActiveTab('privacy');
    } else if (params.tab === 'tos') {
      setActiveTab('tos');
    }
  }, [params.tab]);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <GaStyle />
      <div className="max-w-3xl mx-auto">
        {/* Back only when there is somewhere to go back to (see canGoBack) */}
        {canGoBack && (
          <button
            onClick={() => navigate(-1)}
            className="ga-link flex items-center mb-6"
            style={{ background: "transparent", border: "none", cursor: "pointer" }}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        )}

        {/* Tab buttons */}
        <div className="flex space-x-4 mb-6">
          <button
            className="ga-btn px-4 py-2"
            style={
              activeTab === 'tos'
                ? { color: GA.onAccent, background: GA.accent, border: "none", borderRadius: 999, fontWeight: 600, cursor: "pointer" }
                : { color: GA.text, background: "transparent", border: "1px solid var(--ga-ghost-border)", borderRadius: 999, cursor: "pointer" }
            }
            onClick={() => setActiveTab('tos')}
          >
            Terms of Service
          </button>
          <button
            className="ga-btn px-4 py-2"
            style={
              activeTab === 'privacy'
                ? { color: GA.onAccent, background: GA.accent, border: "none", borderRadius: 999, fontWeight: 600, cursor: "pointer" }
                : { color: GA.text, background: "transparent", border: "1px solid var(--ga-ghost-border)", borderRadius: 999, cursor: "pointer" }
            }
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
        </div>

        {/* Content */}
        <div className="p-8" style={{ ...gaSoftCard, color: GA.body }}>
          <div className="max-w-none">
            {activeTab === 'tos' ? (
              <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(termsOfService) }} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(privacyPolicy) }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;